import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, FormControl,
    InputLabel, Select, MenuItem, Autocomplete, CircularProgress,
    Alert, Snackbar
} from '@mui/material';
import { searchOrdersRequest, assignOrderRequest, resetSearchOrders, resetAssignOrder } from '../../../redux/modules/externalReferrer/slice';

const AssignOrderDialog = ({ open, onClose, externalReferrer }) => {
    const dispatch = useDispatch();
    const { searchOrdersRes, assignOrderRes, loading } = useSelector(state => state.externalReferrer);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [accessLevel, setAccessLevel] = useState('report_and_images');
    const [expiresInDays, setExpiresInDays] = useState(30);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (searchQuery.length >= 2) {
            const timer = setTimeout(() => {
                dispatch(searchOrdersRequest(searchQuery));
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [searchQuery, dispatch]);

    useEffect(() => {
        if (assignOrderRes?.success) {
            setSnackbar({ open: true, message: 'Order access granted successfully', severity: 'success' });
            handleClose();
            dispatch(resetAssignOrder());
        } else if (assignOrderRes && !assignOrderRes.success) {
            setSnackbar({ open: true, message: assignOrderRes.message || 'Error assigning order', severity: 'error' });
            dispatch(resetAssignOrder());
        }
    }, [assignOrderRes]);

    const handleClose = () => {
        setSearchQuery('');
        setSelectedOrder(null);
        setAccessLevel('report_and_images');
        setExpiresInDays(30);
        dispatch(resetSearchOrders());
        onClose();
    };

    const handleAssign = () => {
        if (!selectedOrder || !externalReferrer) return;

        dispatch(assignOrderRequest({
            externalReferrerId: externalReferrer._id,
            orderId: selectedOrder._id,
            accessLevel,
            expiresInDays
        }));
    };

    const orderOptions = searchOrdersRes?.data || [];

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Assign Order to {externalReferrer?.firstName} {externalReferrer?.lastName}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Autocomplete
                            options={orderOptions}
                            getOptionLabel={(option) => `${option.orderId} - ${option.patientName}`}
                            value={selectedOrder}
                            onChange={(e, value) => setSelectedOrder(value)}
                            inputValue={searchQuery}
                            onInputChange={(e, value) => setSearchQuery(value)}
                            loading={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Orders"
                                    placeholder="Search by order ID or patient name..."
                                    helperText="Only orders with completed reports are available"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option._id}>
                                    <Box>
                                        <Typography fontWeight="medium">{option.orderId}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {option.patientName} - {new Date(option.orderDate).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            noOptionsText={searchQuery.length < 2 ? "Type at least 2 characters to search" : "No orders found"}
                        />

                        <FormControl fullWidth sx={{ mt: 3 }}>
                            <InputLabel>Access Level</InputLabel>
                            <Select
                                value={accessLevel}
                                onChange={(e) => setAccessLevel(e.target.value)}
                                label="Access Level"
                            >
                                <MenuItem value="report_and_images">Report and Images (Viewer)</MenuItem>
                                <MenuItem value="report_only">Report Only</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            type="number"
                            label="Access Expires In (Days)"
                            value={expiresInDays}
                            onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                            sx={{ mt: 3 }}
                            inputProps={{ min: 1, max: 365 }}
                            helperText="Access will automatically expire after this many days"
                        />

                        {selectedOrder && (
                            <Alert severity="info" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>Order:</strong> {selectedOrder.orderId}<br />
                                    <strong>Patient:</strong> {selectedOrder.patientName}<br />
                                    <strong>Access:</strong> {accessLevel === 'report_and_images' ? 'Report + DICOM Images' : 'Report Only'}<br />
                                    <strong>Expires:</strong> {new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleAssign}
                        variant="contained"
                        disabled={!selectedOrder || loading}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Grant Access'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AssignOrderDialog;
